import { StyleDeclarationValue, css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { v4 as uuidgen } from 'uuid';
import { SlotMachineAchievementViewModel } from '../../../../aida/viewModels/slotMachines';
import { IModalContext, ModalChildComponentContextKey } from '../../../../models';
import { useAdminUserSession } from '../../../../models/hooks/adminStateHooks';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { AccountUserViewModel, AdminCoffeeSlotMachineViewModel } from '../../../../viewmodels/AdminViewModels';
import { DeprecatedSelect, ISelectOption } from '../../../../web/components/DeprecatedSelect';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { asModalComponent } from '../../../../web/components/Modal';
import { baseStyleSheet } from '../../../../web/styles/styles';

interface IProps extends IModalContext {
	className?: string;
	styles?: StyleDeclarationValue[];
	user?: AccountUserViewModel;
}

const GrantSlotMachineSpinModalBase: React.FC<IProps> = observer(props => {
	const { className, styles = [], user, parentModal } = props;
	const userSession = useAdminUserSession();
	const errorMessages = useErrorMessages();
	const slotMachines = React.useRef<AdminCoffeeSlotMachineViewModel>(
		new AdminCoffeeSlotMachineViewModel(userSession).impersonate({ account: { id: user?.accountId }, user })
	).current;

	const [selectedAchievement, setSelectedAchievement] = React.useState<SlotMachineAchievementViewModel>(null);
	const [count, setCount] = React.useState<number>(1);
	const [busy, setBusy] = React.useState<boolean>(false);

	React.useEffect(() => {
		(async () => {
			try {
				setBusy(true);
				await slotMachines.load();
				setBusy(false);
			} catch (error) {
				setBusy(false);
				errorMessages.pushApiError(error);
			}
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [slotMachines]);

	const achievmentOptions = React.useMemo<ISelectOption<SlotMachineAchievementViewModel>[]>(() => {
		return (slotMachines?.achievments || [])
			.filter(x => x.gameId && x.configuration?.enabled)
			.map(x => {
				return {
					dataContext: x,
					id: uuidgen(),
					text: x.description,
				};
			});
	}, [slotMachines?.achievments]);

	const numberOptions = React.useRef<ISelectOption<number>[]>(
		Array.from({ length: 5 }).map<ISelectOption<number>>((_, i) => {
			return {
				dataContext: i + 1,
				id: uuidgen(),
				text: `${i + 1}`,
			};
		})
	).current;

	const grantSpins = async () => {
		try {
			setBusy(true);

			await slotMachines.grantSlotMachineSpin(user, selectedAchievement, count);
			setBusy(false);
			parentModal?.onRequestClose();
		} catch (error) {
			setBusy(false);
			errorMessages.pushApiError(error);
		}
	};

	const onAchievementOptionChanged = React.useCallback(
		(option: ISelectOption<SlotMachineAchievementViewModel>, wasSelected: boolean) => {
			setSelectedAchievement(wasSelected ? option.dataContext : null);
		},
		[]
	);

	const onRenderAchievementOptionPlaceholder = React.useCallback(() => 'Please select...', []);

	const onCountOptionChanged = React.useCallback((option: ISelectOption<number>, wasSelected: boolean) => {
		setCount(wasSelected ? option.dataContext : 1);
	}, []);

	return (
		<div className={`${css(baseStyleSheet.verticalStack, ...styles)} grant-slot-machine-spin-modal ${className || ''}`}>
			<h4>Grant a spin (for testing)</h4>
			<div>
				<div className={css(baseStyleSheet.tableColumnHeader)}>User:</div>
				<div className={css(baseStyleSheet.horizontalStack)}>
					<span title={user?.email}>{user?.name || ''}</span>
				</div>
			</div>
			<div>
				<div className={css(baseStyleSheet.tableColumnHeader)}>Achievement:</div>
				<DeprecatedSelect
					onOptionClick={onAchievementOptionChanged}
					onRenderPlaceholder={onRenderAchievementOptionPlaceholder}
					options={achievmentOptions}
					selectedOption={achievmentOptions?.find(x => x.dataContext === selectedAchievement)}
				/>
			</div>
			<div>
				<div className={css(baseStyleSheet.tableColumnHeader)}>Count:</div>
				<DeprecatedSelect
					onOptionClick={onCountOptionChanged}
					options={numberOptions}
					selectedOption={numberOptions.find(x => x.dataContext === count)}
				/>
			</div>
			<div className={css(baseStyleSheet.horizontalStack)}>
				<button
					className={css(baseStyleSheet.ctaButton)}
					disabled={!user || slotMachines.isBusy || !selectedAchievement || busy}
					onClick={grantSpins}
				>
					<span>Ok</span>
				</button>
				{busy ? <LoadingSpinner type='tiny' /> : null}
			</div>
		</div>
	);
});

const GrantSlotMachineSpinModalWithContext = inject(ModalChildComponentContextKey)(GrantSlotMachineSpinModalBase);

export const GrantSlotMachineSpinModal = asModalComponent(GrantSlotMachineSpinModalWithContext, {
	shouldCloseOnOverlayClick: false,
	useDefaultHeader: true,
});
